import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppHeader, Footer, AppLauncher, StagingBanner } from '@shared/ui-react';
import type { NavItem, DropdownGroup } from '@shared/ui-react';
import { useAuth } from '@shared/hooks';
import '@shared/ui/base.css';
import { ToastProvider } from './contexts/ToastContext';
import ErrorBoundary from './components/ErrorBoundary';
import { lazy, Suspense } from 'react';

// Lazy load pages for code splitting
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const PipelinesPage = lazy(() => import('./pages/PipelinesPage'));
const FeatureFlagsPage = lazy(() => import('./pages/FeatureFlagsPage'));
const MergeQueuePage = lazy(() => import('./pages/MergeQueuePage'));
const AgentsPage = lazy(() => import('./pages/AgentsPage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const MetricsPage = lazy(() => import('./pages/MetricsPage'));
const TracesPage = lazy(() => import('./pages/TracesPage'));
const OSINTPage = lazy(() => import('./pages/OSINTPage'));

const queryClient = new QueryClient();

// Loading fallback for lazy routes
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <div className="mt-4 text-gray-600">Loading page...</div>
    </div>
  </div>
);

const GlobalOverlay = ({ visible, label }: { visible: boolean; label?: string }) => {
  if (!visible) return null;
  return (
    <div className="overlay">
      <div className="overlay-box">
        <div className="spinner big" />
        <div className="label">{label ?? 'Bitte warten…'}</div>
      </div>
    </div>
  );
};

function App() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (authLoading) {
    return (
      <>
        <div className="app-shell" />
        <GlobalOverlay visible label="Session wird geprüft…" />
      </>
    );
  }

  if (!user) {
    return (
      <>
        <div className="app-shell" />
        <GlobalOverlay visible label="Weiterleitung zu Login…" />
      </>
    );
  }

  const navItems: NavItem[] = [
    { label: 'Dashboard', to: '/dashboard', icon: '📊' },
    { label: 'Pipelines', to: '/pipelines', icon: '🔄' },
    { label: 'OSINT', to: '/osint', icon: '🦉' },
    { label: 'Tasks', to: '/tasks', icon: '📋' },
  ];

  const dropdownGroups: DropdownGroup[] = [
    {
      label: 'Management',
      items: [
        { label: 'Feature Flags', to: '/flags', icon: '🚩' },
        { label: 'Merge Queue', to: '/queue', icon: '📋' },
        { label: 'AI Agents', to: '/agents', icon: '🤖' },
      ],
    },
    {
      label: 'Analytics',
      items: [
        { label: 'Metrics', to: '/metrics', icon: '📈' },
        { label: 'Traces', to: '/traces', icon: '🔍' },
      ],
    },
  ];

  return (
    <>
      <StagingBanner />
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          {/* Skip Navigation Link for Accessibility */}
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:top-4 focus:left-4"
          >
            Skip to main content
          </a>

          <div className="app-layout">
            <AppLauncher
            isAuthenticated={true}
            currentApp="pipesquid"
            position="left"
            enableParallax={true}
          />

          <AppHeader
            appName="weretradeIT"
            subtitle="PipeSquid"
            logoUrl="https://intranet.weretrade.com/logos/pipesquid.svg"
            variant="glass"
            sticky
            navItems={navItems}
            dropdownGroups={dropdownGroups}
            activeItem={location.pathname}
            showThemeToggle
            showUserWidget
            onNavClick={(to) => navigate(to)}
          />

          <main id="main-content" className="app-main">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={
                  <ErrorBoundary>
                    <DashboardPage />
                  </ErrorBoundary>
                } />
                <Route path="/pipelines" element={
                  <ErrorBoundary>
                    <PipelinesPage />
                  </ErrorBoundary>
                } />
                <Route path="/osint" element={
                  <ErrorBoundary>
                    <OSINTPage />
                  </ErrorBoundary>
                } />
                <Route path="/flags" element={
                  <ErrorBoundary>
                    <FeatureFlagsPage />
                  </ErrorBoundary>
                } />
                <Route path="/queue" element={
                  <ErrorBoundary>
                    <MergeQueuePage />
                  </ErrorBoundary>
                } />
                <Route path="/agents" element={
                  <ErrorBoundary>
                    <AgentsPage />
                  </ErrorBoundary>
                } />
                <Route path="/tasks" element={
                  <ErrorBoundary>
                    <TasksPage />
                  </ErrorBoundary>
                } />
                <Route path="/metrics" element={
                  <ErrorBoundary>
                    <MetricsPage />
                  </ErrorBoundary>
                } />
                <Route path="/traces" element={
                  <ErrorBoundary>
                    <TracesPage />
                  </ErrorBoundary>
                } />
              </Routes>
            </Suspense>
          </main>

            <Footer />
          </div>
        </ToastProvider>
      </QueryClientProvider>
    </>
  );
}

export default App;
